import React from 'react'
import truncate from 'lodash/truncate'
import {
  Center,
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  useColorModeValue,
  WrapItem,
  Wrap,
} from '@chakra-ui/react'
import {
  TwitterShareButton,
  TwitterIcon,
  FacebookShareButton,
  FacebookIcon,
} from 'react-share'
import { BsCircle } from 'react-icons/bs'
import { ImCross } from 'react-icons/im'
import { Playlist } from 'types/generated/graphql'

type Props = {
  playlist: Playlist
  quizzesCount: number
  score: number
  answers: string[]
}

export const EmbedResult: React.FC<Props> = ({
  playlist,
  quizzesCount,
  score,
  answers,
}) => {
  const buttonNextColor = useColorModeValue('buttonNextLight', 'buttonNextDark')
  const { origin } = new URL(window.location.href)
  const shareUrl = `${origin}/quizplaylist/${playlist.id}/play`
  const sendOnLink = playlist.snsInfo.destinationLink || ''
  const sendOnLinkText =
    truncate(playlist.snsInfo.destinationLinkText || '', { length: 25 }) ||
    'リンク'

  const snsShareText = (() => {
    const resultMsg = `${quizzesCount}問中${score}問正解でした！`
    if (playlist.snsInfo.shareText) {
      return [playlist.snsInfo.shareText, resultMsg].join('\n')
    }
    return [playlist.name, resultMsg].join('\n')
  })()

  // 合格点設定によるテキスト表示の分岐
  const resultTextComponent = () => {
    // 合格点がクイズ数を上回った場合は満点を合格点とする
    let passingScore = playlist.passingScore || 0
    if (passingScore > quizzesCount) {
      passingScore = quizzesCount
    }

    // 得点が合格点を上回っているか否かを判定し、それぞれの場合に設定された表示文があればその文字列を表示、なければ何も表示しない
    const displayText =
      (score >= passingScore && playlist.passText) ||
      (score < passingScore && playlist.failText)

    if (displayText) {
      return (
        <Center w="100%" p={2} marginY={10}>
          <Text fontSize="md">{displayText}</Text>
        </Center>
      )
    }
  }

  return (
    <VStack
      w={{ base: '100%', md: '60%', sm: '100%' }}
      minHeight="480px"
      spacing={4}
      justify="center"
    >
      <Heading as="h3" size="md" pb="0.5rem" m="0">
        クイズ結果
      </Heading>
      <Heading as="h3" size="md" color="red">
        {score}/{quizzesCount}正解！
      </Heading>

      {resultTextComponent()}

      <Wrap spacing={3}>
        {playlist.quizzes.map((quiz, index) => {
          return (
            <WrapItem key={index}>
              <VStack>
                <Text fontSize="sm">Q{index + 1}</Text>
                <Heading
                  as="h3"
                  size="md"
                  m="0"
                  color={
                    answers[index] === quiz.content.correct
                      ? '#dc143c'
                      : '#4169e1'
                  }
                >
                  {answers[index] === quiz.content.correct ? (
                    <BsCircle />
                  ) : (
                    <ImCross />
                  )}
                </Heading>
              </VStack>
            </WrapItem>
          )
        })}
      </Wrap>

      <HStack>
        <TwitterShareButton
          url={shareUrl}
          title={snsShareText}
          hashtags={['qukker']}
        >
          <TwitterIcon size={32} borderRadius={10} />
        </TwitterShareButton>
        <FacebookShareButton
          url={shareUrl}
          quote={snsShareText}
          hashtag={'qukker'}
        >
          <FacebookIcon size={32} borderRadius={10} />
        </FacebookShareButton>
      </HStack>

      {sendOnLink && (
        <Button
          backgroundColor={buttonNextColor}
          mb={3}
          as="a"
          href={sendOnLink}
          target="_blank"
        >
          {sendOnLinkText}
        </Button>
      )}
    </VStack>
  )
}
